import { useState, useEffect, useCallback, useRef } from "react";
import {
  listSessions,
  listChats,
  listMensajes,
  sendMessageFromRole,
  assignContactoToRole,
  assignChatToRole,
  checkAccess,
} from "../services/whatsapp";
import type { WhatsAppSession, Chat, Mensaje } from "../types/whatsapp";
import { getRolInfo } from "../types/whatsapp";

const POLLING_INTERVAL = 4000; // 4 seconds

function ultimoMensajeTexto(chat: Chat): string {
  const um = chat.ultimo_mensaje;
  if (!um) return "Sin mensajes";
  if (typeof um === "string") return um;
  return (um as { text?: string }).text || "...";
}

export default function ChatsPage() {
  // Sessions & role selector
  const [sessions, setSessions] = useState<WhatsAppSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(true);

  // Chats
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [loadingChats, setLoadingChats] = useState(false);

  // Messages
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [accessGranted, setAccessGranted] = useState(true);

  // New chat / assign
  const [showNewChat, setShowNewChat] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Polling refs
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const activeSession = sessions.find((s) => (s.id_whatsapp_session || s.id) === activeSessionId) || null;
  const activeRolInfo = activeSession ? getRolInfo(activeSession.rol) : null;

  // Auto-scroll messages to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [mensajes]);

  // Load sessions on mount
  useEffect(() => {
    const fetch = async () => {
      setLoadingSessions(true);
      try {
        const data = await listSessions();
        const arr = Array.isArray(data) ? data : [];
        setSessions(arr);
        const connected = arr.find((s) => s.status === "connected");
        if (connected) {
          setActiveSessionId(connected.id_whatsapp_session || connected.id || null);
        } else if (arr.length > 0) {
          setActiveSessionId(arr[0]!.id_whatsapp_session || arr[0]!.id || null);
        }
      } catch {
        // ignore
      } finally {
        setLoadingSessions(false);
      }
    };
    fetch();
  }, []);

  // Load chats when active session changes
  const fetchChats = useCallback(async (sessionId: string) => {
    setLoadingChats(true);
    try {
      const data = await listChats({ sesion_id: sessionId, ordering: "-fecha_modificacion" });
      setChats(Array.isArray(data) ? data : []);
    } catch {
      setChats([]);
    } finally {
      setLoadingChats(false);
    }
  }, []);

  // Fetch messages for selected chat
  const fetchMessages = useCallback(async (chatId: string, sessionId: string) => {
    try {
      const data = await listMensajes({ chat: chatId, sesion_id: sessionId, ordering: "whatsapp_timestamp" });
      setMensajes(Array.isArray(data) ? data : []);
    } catch {
      // keep existing
    }
  }, []);

  useEffect(() => {
    if (!activeSessionId) {
      setChats([]);
      setSelectedChat(null);
      setMensajes([]);
      return;
    }
    fetchChats(activeSessionId);
  }, [activeSessionId, fetchChats]);

  // Polling: refresh chats and messages every 4 seconds
  useEffect(() => {
    if (!activeSessionId) return;

    // Clear previous interval
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(() => {
      if (activeSessionId) {
        fetchChats(activeSessionId);
        if (selectedChat?.id_chat) {
          fetchMessages(selectedChat.id_chat, activeSessionId);
        }
      }
    }, POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [activeSessionId, selectedChat?.id_chat, fetchChats, fetchMessages]);

  const selectChat = async (chat: Chat) => {
    setSelectedChat(chat);
    if (!activeSessionId) return;

    // First verify access before loading messages
    try {
      if (chat.id_chat) {
        const accessResult = await checkAccess(chat.id_chat);
        setAccessGranted(accessResult.status === "success" || accessResult.data?.allowed !== false);
      }
    } catch {
      setAccessGranted(true); // assume granted if endpoint fails
    }

    try {
      const data = await listMensajes({ chat: chat.id_chat, sesion_id: activeSessionId, ordering: "whatsapp_timestamp" });
      setMensajes(Array.isArray(data) ? data : []);
    } catch {
      setMensajes([]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChat || !activeSessionId) return;

    // Verify access before sending
    try {
      if (selectedChat.id_chat) {
        const accessResult = await checkAccess(selectedChat.id_chat);
        if (accessResult.status !== "success" && accessResult.data?.allowed === false) {
          setError("No tienes permiso para enviar mensajes en este chat");
          return;
        }
      }
    } catch {
      // proceed anyway if check fails
    }

    setSending(true);
    setError(null);
    try {
      const jid = selectedChat.jid || "";
      const phoneDestino = jid.split("@")[0] || jid;

      const response = await sendMessageFromRole({
        id_sesion: activeSessionId,
        phone_destino: phoneDestino,
        texto: newMessage,
      });

      setNewMessage("");
      // Refresh messages
      if (selectedChat.id_chat) {
        const data = await listMensajes({ chat: selectedChat.id_chat, sesion_id: activeSessionId, ordering: "whatsapp_timestamp" });
        setMensajes(Array.isArray(data) ? data : []);
      }

      if (response.status !== "success" && response.status !== undefined) {
        setError(response.message || "Error al enviar mensaje");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al enviar mensaje");
    } finally {
      setSending(false);
    }
  };

  const handleCreateNewChat = async () => {
    if (!newChatPhone.trim() || !activeSessionId) return;
    setSending(true);
    setError(null);
    try {
      const response = await assignContactoToRole({
        phone_contacto: newChatPhone,
        id_sesion: activeSessionId,
      });
      setNewChatPhone("");
      setShowNewChat(false);
      await fetchChats(activeSessionId);
      if (response.status !== "success" && response.status !== undefined) {
        setError(response.message || "Error al crear chat");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear chat");
    } finally {
      setSending(false);
    }
  };

  const handleReassignChat = async (chatId: string, targetSessionId: string) => {
    try {
      const response = await assignChatToRole({
        id_chat: chatId,
        id_sesion: targetSessionId,
      });
      await fetchChats(activeSessionId!);
      if (selectedChat?.id_chat === chatId) {
        setSelectedChat(null);
        setMensajes([]);
      }
      if (response.status !== "success" && response.status !== undefined) {
        setError(response.message || "Error al reasignar chat");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al reasignar chat");
    }
  };

  const connectedSessions = sessions.filter((s) => s.status === "connected");
  const availableSessions = sessions.filter((s) => (s.id_whatsapp_session || s.id) !== activeSessionId);

  return (
    <div className="chats-page-layout">
      {/* Left sidebar: Chat list + Role selector */}
      <div className="chats-sidebar">
        {/* Role selector */}
        <div className="chats-header">
          <h2 className="page-title">Chats</h2>
          <p className="page-subtitle">Selecciona un rol activo</p>

          {loadingSessions ? (
            <div className="loading-state" style={{ padding: "1rem" }}>
              <div className="spinner" />
            </div>
          ) : connectedSessions.length === 0 ? (
            <div className="alert" style={{ marginTop: "0.5rem", fontSize: "0.8rem", background: "rgba(234, 179, 8, 0.1)", border: "1px solid rgba(234, 179, 8, 0.3)", color: "#eab308" }}>
              No hay roles conectados. Ve a Sesiones y escanea el QR.
            </div>
          ) : (
            <div className="role-selector">
              {connectedSessions.map((session) => {
                const rolInfo = getRolInfo(session.rol);
                const sessionId = session.id_whatsapp_session || session.id || "";
                const isActive = sessionId === activeSessionId;
                return (
                  <button
                    key={sessionId}
                    className={`role-tab ${isActive ? "active" : ""}`}
                    onClick={() => setActiveSessionId(sessionId || null)}
                    style={{
                      borderColor: isActive ? rolInfo.color : "transparent",
                      background: isActive ? rolInfo.color + "15" : "transparent",
                    }}
                  >
                    <span className="role-tab-dot" style={{ background: rolInfo.color }} />
                    <span className="role-tab-label">{rolInfo.label}</span>
                    {session.label && <span className="role-tab-sub">{session.label}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Add chat button */}
        <div className="chats-toolbar">
          <div className="chats-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input type="text" placeholder="Buscar conversación..." />
          </div>
          <button className="btn btn-sm btn-primary" onClick={() => setShowNewChat(!showNewChat)} title="Nuevo chat">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {/* New chat form */}
        {showNewChat && (
          <div className="new-chat-form">
            <div className="form-group">
              <input
                type="text"
                value={newChatPhone}
                onChange={(e) => setNewChatPhone(e.target.value)}
                placeholder="Teléfono del contacto (Ej: 59399...)"
              />
            </div>
            <div className="form-actions" style={{ justifyContent: "flex-start" }}>
              <button className="btn btn-sm btn-primary" onClick={handleCreateNewChat} disabled={sending || !newChatPhone.trim()}>
                {sending ? "..." : "Asignar a este rol"}
              </button>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowNewChat(false)}>
                Cancelar
              </button>
            </div>
          </div>
        )}

        {error && <div className="alert alert-error" style={{ margin: "0.5rem" }}>{error}</div>}

        {/* Polling indicator */}
        <div className="polling-indicator">
          <span className="polling-dot" />
          <span className="polling-text">Actualizando cada 4s</span>
        </div>

        {/* Chat list */}
        {activeSessionId && loadingChats ? (
          <div className="loading-state">
            <div className="spinner" />
            <span>Cargando chats...</span>
          </div>
        ) : activeSessionId && chats.length === 0 ? (
          <div className="empty-state">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h3>No hay chats</h3>
            <p>Crea un nuevo chat asignando un contacto a este rol</p>
          </div>
        ) : activeSessionId ? (
          <div className="chats-list">
            {chats.map((chat) => (
              <div
                key={chat.id_chat}
                className={`chat-list-item ${selectedChat?.id_chat === chat.id_chat ? "active" : ""}`}
                onClick={() => selectChat(chat)}
              >
                <div className="chat-list-avatar">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div className="chat-list-info">
                  <span className="chat-list-name">{chat.jid?.split("@")[0] || chat.codigo || "Sin nombre"}</span>
                  <span className="chat-list-preview">{ultimoMensajeTexto(chat)}</span>
                </div>
                {availableSessions.length > 0 && (
                  <div className="chat-reassign" onClick={(e) => e.stopPropagation()}>
                    <select
                      className="reassign-select"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value && chat.id_chat) {
                          handleReassignChat(chat.id_chat, e.target.value);
                          e.target.value = "";
                        }
                      }}
                    >
                      <option value="" disabled>Reasignar a...</option>
                      {availableSessions.map((s) => {
                        const r = getRolInfo(s.rol);
                        const sid = s.id_whatsapp_session || s.id || "";
                        return (
                          <option key={sid} value={sid}>
                            {r.label} {s.label ? `(${s.label})` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>Selecciona un rol conectado para ver sus chats</p>
          </div>
        )}
      </div>

      {/* Right: Chat window */}
      <div className="chat-window-panel">
        {!activeSessionId ? (
          <div className="empty-state chat-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h3>Selecciona un rol</h3>
            <p>Elige un rol activo de la lista para ver sus conversaciones</p>
          </div>
        ) : selectedChat ? (
          <>
            <div className="chat-window-header">
              <div>
                <h3>{selectedChat.jid?.split("@")[0] || selectedChat.codigo}</h3>
                <span className="chat-window-meta">
                  {activeRolInfo && <span style={{ color: activeRolInfo.color }}>{activeRolInfo.label}</span>}
                  {selectedChat.kind === "group" && " · Grupo"}
                  {!accessGranted && <span className="access-denied"> · Sin acceso</span>}
                </span>
              </div>
            </div>

            <div className="chat-messages">
              {mensajes.length === 0 ? (
                <div className="empty-state">
                  <p>No hay mensajes en esta conversación</p>
                </div>
              ) : (
                // Mostramos en orden cronológico (más antiguo arriba, más reciente abajo)
                mensajes.map((msg) => (
                  <div
                    key={msg.id_mensaje}
                    className={`message-bubble ${msg.from_me ? "sent" : "received"}`}
                  >
                    <div className="message-text">{msg.text}</div>
                    <div className="message-meta">
                      <span>{msg.message_type || "text"}</span>
                      {msg.whatsapp_timestamp && (
                        <span>{new Date(msg.whatsapp_timestamp).toLocaleString()}</span>
                      )}
                      {msg.status && (
                        <span className={`message-status ${msg.status}`}>{msg.status}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-area">
              <div className="chat-input-rol-badge" style={{ borderColor: activeRolInfo?.color || "#64748b" }}>
                <span className="chat-input-rol-dot" style={{ background: activeRolInfo?.color || "#64748b" }} />
                <span>{activeRolInfo?.label || "Rol"}</span>
              </div>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder="Escribe un mensaje..."
              />
              <button
                className="btn btn-primary btn-send"
                onClick={handleSendMessage}
                disabled={sending || !newMessage.trim() || !accessGranted}
              >
                {sending ? (
                  <div className="spinner-sm" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="empty-state chat-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <h3>Selecciona un chat</h3>
            <p>Elige una conversación de la lista para ver sus mensajes</p>
          </div>
        )}
      </div>
    </div>
  );
}